import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Bell, Menu, X, User, Settings, LogOut, Shield } from 'lucide-react';
import { useAuth } from '../lib/auth';

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <nav className="bg-white border-b border-gray-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 text-blue-600 font-bold text-xl">
            <Bell className="w-6 h-6" />
            <span>RemindMeOf</span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-6">
            <Link to="/blog" className="text-gray-600 hover:text-blue-600 font-medium transition-colors">
              Tips & Guides
            </Link>
            <Link to="/about" className="text-gray-600 hover:text-blue-600 font-medium transition-colors">
              About
            </Link>
            <Link to="/contact" className="text-gray-600 hover:text-blue-600 font-medium transition-colors">
              Contact
            </Link>
            {user ? (
              <>
                <Link to="/dashboard" className="text-gray-600 hover:text-blue-600 font-medium transition-colors">
                  My Reminders
                </Link>
                {user.is_admin ? (
                  <>
                    <Link to="/admin/users" className="text-gray-600 hover:text-blue-600 font-medium transition-colors flex items-center gap-1">
                      <Shield className="w-4 h-4" />
                      Admin
                    </Link>
                    <Link to="/admin/report" className="text-gray-600 hover:text-blue-600 font-medium transition-colors">
                      Report
                    </Link>
                  </>
                ) : null}
                <div className="relative">
                  <button
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                    className="flex items-center gap-2 bg-blue-50 text-blue-700 rounded-full px-3 py-1.5 font-medium hover:bg-blue-100 transition-colors"
                  >
                    <User className="w-4 h-4" />
                    <span className="max-w-[140px] truncate">{user.full_name || user.email}</span>
                  </button>
                  {userMenuOpen && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg border border-gray-100 py-1 z-50">
                      <Link
                        to="/profile"
                        onClick={() => setUserMenuOpen(false)}
                        className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        <Settings className="w-4 h-4" /> Profile & Settings
                      </Link>
                      <button
                        onClick={handleLogout}
                        className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                      >
                        <LogOut className="w-4 h-4" /> Sign Out
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                <Link to="/login" className="text-gray-600 hover:text-blue-600 font-medium transition-colors">
                  Sign In
                </Link>
                <Link to="/register" className="btn-primary text-sm">
                  Get Started Free
                </Link>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            className="md:hidden p-2 text-gray-600"
            onClick={() => setMenuOpen(!menuOpen)}
          >
            {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="md:hidden border-t border-gray-200 bg-white px-4 py-4 space-y-3">
          {user ? (
            <>
              <div className="text-sm font-medium text-gray-500">{user.email}</div>
              <Link to="/dashboard" onClick={() => setMenuOpen(false)} className="block py-2 text-gray-700 font-medium">My Reminders</Link>
              <Link to="/profile" onClick={() => setMenuOpen(false)} className="block py-2 text-gray-700 font-medium">Profile & Settings</Link>
              {user.is_admin ? (
                <>
                  <Link to="/admin/users" onClick={() => setMenuOpen(false)} className="block py-2 text-gray-700 font-medium">Admin Panel</Link>
                  <Link to="/admin/report" onClick={() => setMenuOpen(false)} className="block py-2 text-gray-700 font-medium">Report</Link>
                </>
              ) : null}
              <button onClick={handleLogout} className="block w-full text-left py-2 text-red-600 font-medium">Sign Out</button>
            </>
          ) : (
            <>
              <Link to="/login" onClick={() => setMenuOpen(false)} className="block py-2 text-gray-700 font-medium">Sign In</Link>
              <Link to="/register" onClick={() => setMenuOpen(false)} className="block py-2 text-blue-600 font-medium">Get Started Free</Link>
            </>
          )}
          <Link to="/blog" onClick={() => setMenuOpen(false)} className="block py-2 text-gray-600 font-medium">Tips & Guides</Link>
          <Link to="/about" onClick={() => setMenuOpen(false)} className="block py-2 text-gray-600 font-medium">About Us</Link>
          <Link to="/contact" onClick={() => setMenuOpen(false)} className="block py-2 text-gray-600 font-medium">Contact</Link>
          <Link to="/privacy" onClick={() => setMenuOpen(false)} className="block py-2 text-gray-600 font-medium">Privacy Policy</Link>
          <Link to="/terms" onClick={() => setMenuOpen(false)} className="block py-2 text-gray-600 font-medium">Terms & Conditions</Link>
        </div>
      )}
    </nav>
  );
}
